import angular, { ILogService, IQService, ITimeoutService, IWindowService } from 'angular'

import templateUrl from './edit-employee.component.html';
import { MinimumWageReviewModal } from '../../wage-policy/minimum-wage-review-modal.component';
import { SiteModuleEnum } from '../../../core/enum/site-module';
import { sortIcdOfficeByOwner, IcdOfficeService } from '../../../core/services/data-services/icd-office.service';
import { FeatureManager } from '../../../core/feature-flags/feature-manager.service';
import { UtilityService } from '../../../core/services/utility.service';
import { PayrollService, LocaleService, EmployeeService } from '../../../core/services';
import { getExceptionMessage } from '../../../core/data-utils';

export const EditEmployeeComponent = {
    templateUrl,
    controller: EditEmployeeController,
    controllerAs: "vm",
    bindings: {
        employeeID: "<employeeId",
        icdOfficeID: "<icdOfficeId",
        employee: "<",
        featureManager: "<featureManagerSync",
        afterSave: "&",
        overrideCreateUpdate: "<",
        isSelfSignUp: "<",
        showPasswordEditor: "<"
    }
}

function EditEmployeeController($uibModal, $q: IQService, $timeout: ITimeoutService, $log: ILogService,
    $window: IWindowService, employeeService: EmployeeService, featureManager: FeatureManager,
    icdOfficeService: IcdOfficeService, localeService: LocaleService,
    payrollService: PayrollService, utilityService: UtilityService,
    wagePolicyService, employmentRecordEditContextFactory) {
    'ngInject'

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const vm = this;
    const ROSTER_MODULE = 3;

    vm.loaded = false;
    vm.employeeID;
    vm.employee;
    vm.employeeForm;
    vm.roleAccess;
    vm.payrollAccess;
    vm.globalRoles;
    vm.originalEmailAddress;
    vm.userID;
    vm.icdOfficeID;
    vm.sendUserPassword = true;

    vm.logEmployeeForm = function () {
        console.log(vm.employeeForm);
    };

    vm.getEmployeeModel;
    vm.getCompensationProfiles;
    vm.getAssignableRoles;
    vm.getWagePolicies;
    vm.getTerminationReasons;
    vm.loadEmployeeIcdOffices;
    vm.loadEmployeeGlobalRoles;
    vm.lockImplicitModuleAdminStatuses;
    vm.handleSubmit = handleSubmit;
    vm.sendPasswordReminder = sendPasswordReminder;
    vm.showMinimumWageRequirements = showMinimumWageRequirements;
    vm.cancelOrBackToList = cancelOrBackToList;
    vm.onChangeSendUserPassword = onChangeSendUserPassword;
    vm.$onInit = $onInit;

    ///////

    function $onInit() {
        if (!vm.featureManager) {
            vm.featureManager = featureManager;
            featureManager.ensureLoaded();
        }
        utilityService.showLoading();

        // Load all general data that will be needed from the API services.
        if (vm.employee) {
            // Use the employee passed into the directive.
            vm.getEmployeeModel = $q.when(vm.employee);
        } else if (vm.employeeID) {
            vm.getEmployeeModel = employeeService.retrieveEmployee(vm.employeeID)
                .then(function (employee) {
                    // We don't want to reset the employee's password to its own hash.
                    employee.Password = null;
                    return employee;
                });
        } else {
            vm.getEmployeeModel = employeeService.retrieveNewEmployeeModel();
        }

        const employmentStatuses = employeeService.retrieveEmployeeStatuses();
        const moduleAdminIcdOffices = icdOfficeService.retrieveIcdOffices({ moduleID: ROSTER_MODULE })
            .then(icdOffices => icdOffices.sort(sortIcdOfficeByOwner));
        const jobTitles = employeeService.retrieveJobTitles();
        const payFrequencies = payrollService.retrievePayFrequencies();
        const departments = employeeService.retrieveCompensationProfiles();
        // These are the assignable roles that the current user is able to assign to any office.
        const assignableRoles = employeeService.retrieveEmployeeAssignableRoles().then(loadRoleDescriptions);
        vm.getWagePolicies = wagePolicyService.retrieveTodayWagePolicies();
        const terminationReasons = employeeService.retrieveTerminationReasons();

        const roleAccessLoaded = employeeService.retrievePermissions({
            module: SiteModuleEnum.Roster
        }).then(roleAccess => vm.roleAccess = roleAccess);

        const payrollAccessLoaded = employeeService.retrievePermissions({
            stateName: "report.payrollProcessing"
        }).then(payrollAccess => vm.payrollAccess = payrollAccess);

        const identityLoaded = employeeService.retrieveIdentity()
            .then(identity => {
                vm.userID = identity.EmployeeID;
                vm.identity = identity;
            });

        // Wait for the rest of the initial data loads to complete.
        $q.all([
            vm.getEmployeeModel,
            employmentStatuses,
            moduleAdminIcdOffices,
            jobTitles,
            payFrequencies,
            departments,
            assignableRoles,
            terminationReasons,
            vm.getWagePolicies,
            roleAccessLoaded,
            payrollAccessLoaded,
            identityLoaded
        ])
            .then(function ([employee, statuses, offices, jobTitles, payFrequencies, departments,
                roles, terminationReasons, wagePolicies]) {

                vm.employee = employee;

                // Preload employment record to a specific office.
                if (vm.icdOfficeID != null) {
                    // Pre-select the office appearing in the dropdown.
                    employee.IcdOffices.forEach(function (office) {
                        // If the office is unset, pre-fill it as the office from the query string.
                        if (!office.IcdOfficeID) {
                            office.IcdOfficeID = vm.icdOfficeID;
                        }
                    });
                }

                if (!employee.GlobalRoles.length) {
                    // Change global roles from a roleID dict to an array.
                    employee.GlobalRoles = roles.filter(p => p.GlobalRole)
                        .map(function (role) {
                            return mapEmployeeRole(role, !!employee.GlobalRoles[role.Id]);
                        });
                }

                vm.context = employmentRecordEditContextFactory(employee, offices, statuses,
                    terminationReasons, payFrequencies, jobTitles, departments, wagePolicies);

                vm.pristineEmployee = angular.copy(vm.employee);
                vm.originalUserName = vm.employee.UserName;
                vm.originalEmailAddress = vm.employee.EmailAddress;
            })
            .catch(function (ex) {
                console.error(ex);
                alert("Initial load failed: " + getExceptionMessage(ex));
            })
            .then(function () {
                vm.loaded = true;
                utilityService.hideLoading();
            });

        $window.onbeforeunload = function () {
            // Ask before navigating away if the form is dirty.
            return vm.employeeForm && vm.employeeForm.$dirty ? "You have unsaved changes that will be lost after leaving this page." : undefined;
        };
    }

    function onChangeSendUserPassword() {
        if (vm.sendUserPassword) {
            vm.manualPassword = vm.employee.Password;
            vm.employee.Password = null;
        } else {
            vm.employee.Password = vm.manualPassword;
        }
    }

    function handleSubmit() {
        let submitForm = $q.when(vm.employee.Id);
        if (vm.employeeForm.$invalid) {
            alert("Please fix any validation errors and try again.");
            return;
        }

        if (vm.userID === vm.employeeID && vm.employee.UserName !== vm.originalUserName
            && !confirm("You must be logged out of your current session in order to complete "
                + "this change. Please log in with the new user name to continue.")) {
            return;
        }

        // If this user was created via onboarding and this is the first time saving on this
        // page, always set IsOnPayroll to true when the checkbox is hidden.
        if (!vm.identity.IsSiteAdmin && !vm.employee.IsComplete) {
            vm.employee.IcdOffices.forEach(p => p.IsOnPayroll = true);
        }

        vm.employee.SendWelcomeEmail = vm.sendUserPassword;

        if (vm.employeeForm.$dirty) {
            utilityService.showLoading();
            submitForm = employeeService.saveEmployee(vm.employee)
                .then(function (employee) {
                    utilityService.hideLoading();
                    return employee.Id;
                })
                .catch(function (ex) {
                    utilityService.hideLoading();
                    throw ex;
                });
        } else if (vm.employee.IsNew) {
            // Shouldn't submit the form if the employee is new and the form is pristine.
            return;
        }
        submitForm.then(function (employeeID) {
            vm.employeeID = employeeID;
            vm.employee.EmployeeID = employeeID;
            vm.employeeForm.$setPristine();
            // Redirect to the next page.
            vm.afterSave({ employee: vm.employee, window: window });
        })
            .catch(function (ex) {
                if (ex.status === 401) {
                    // Expected if the user changed their own user name.
                    vm.employeeForm.$setPristine();
                    // Reload the page, which will redirect to the signin form.
                    window.location.reload();
                    return;
                }
                $log.error(ex);
                vm.modelState = ex.data ? ex.data.ModelState : null;
                alert("Saving failed: " + utilityService.getExceptionMessage(ex));
            });

    }

    function sendPasswordReminder() {
        if (vm.employee.EmailAddress !== vm.originalEmailAddress) {
            if (!confirm(`The email will be sent to ${vm.originalEmailAddress}. `
                + ` To send it to ${vm.employee.EmailAddress},`
                + ` save changes before resetting the password.`)) {
                return;
            }
        }

        utilityService.showLoadingUntilResolved(
            employeeService.sendPasswordReminder(vm.employee.UserName)
                .then(function () {
                    alert("Password Reset Message Sent");
                })
                .catch(function () {
                    alert("Error Sending Password Message");
                })
        );
    }

    function showMinimumWageRequirements(office) {
        utilityService.showLoading();
        return $q.all([loadIcdOfficeWagePolicies(office.IcdOffice), vm.getWagePolicies])
            .then(function (v) {
                utilityService.hideLoading();
                const icdOffice = v[0];
                const wagePolicies = v[1];

                return $uibModal.open({
                    ...MinimumWageReviewModal,
                    resolve: {
                        wagePolicies: () => wagePolicies,
                        selectedOffice: () => icdOffice
                    }
                }).result;

            })
            .catch(utilityService.hideLoading);
    }

    function cancelOrBackToList() {
        vm.employee = angular.copy(vm.pristineEmployee);
        $timeout(function () {
            vm.employeeForm.$setPristine();
        });
    }

    function mapEmployeeRole(role, isInRole) {
        return {
            RoleID: role.Id,
            Name: role.Name,
            Description: role.Description,
            IsInRole: isInRole,
            AdministeredModules: role.AdministeredModules
        };
    }

    function loadIcdOfficeWagePolicies(icdOffice) {
        return $q.all([$q.when(icdOffice), vm.getWagePolicies]).then(function (v) {
            const icdOffice = v[0];
            const wagePolicies = v[1];

            if (!icdOffice) {
                return null;
            }
            // Only perform lookup once.
            if (!icdOffice.WagePolicy) {
                icdOffice.WagePolicy = wagePolicies.filter(wagePolicy =>
                    wagePolicy.StateName === icdOffice.OfficeState)[0];
            }
            return icdOffice;
        });
    }

    /**
     * Load any dynamic description data associated with the list of roles.
     * @param {Promise<Role>} roles The list of roles to load.
     * @returns {Promise<Role[]>} The resolved role with localized descriptions loaded.
     */
    function loadRoleDescriptions(roles) {
        return $q.when(roles)
            .then(function (roles) {
                return $q.all(roles.map(function (role) {
                    if (!role.Description || role.Description[0] !== "|") {
                        return $q.when(role);
                    }
                    // Translate the description using the locale service.
                    return localeService.retrieveCurrentLocaleTranslations()
                        .then(function (translations) {
                            role.Description = translations[role.Description.slice(1)];
                            return role;
                        });
                }));
            });
    }
}
